import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import PresenceIndicator from "@/components/PresenceIndicator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowRight,
  Ban,
  Check,
  ChevronRight,
  Flag,
  Keyboard,
  ListChecks,
  SkipForward,
  Timer,
  X,
} from "lucide-react";
import { toast } from "sonner";

function formatDuration(ms) {
  if (!ms || ms < 0) return "0s";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s % 60}s`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

export default function RunnerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [session, setSession] = useState(null);
  const [results, setResults] = useState([]);
  const [index, setIndex] = useState(0);
  const [notes, setNotes] = useState("");
  const [failDialogOpen, setFailDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const caseStartRef = useRef(Date.now());
  const notesRef = useRef(null);

  // Flatten cases with section info
  const flatCases = useMemo(() => {
    if (!checklist) return [];
    const arr = [];
    checklist.sections.forEach((s) => {
      s.cases.forEach((c) =>
        arr.push({ ...c, sectionTitle: s.title, sectionNumber: s.number })
      );
    });
    return arr;
  }, [checklist]);

  useEffect(() => {
    (async () => {
      try {
        const [cl, sess] = await Promise.all([api.getChecklist(), api.getSession(id)]);
        setChecklist(cl);
        setSession(sess.session);
        setResults(sess.results);
        setIndex(sess.session.current_index || 0);
      } catch (e) {
        toast.error("Failed to load session");
        navigate("/dashboard");
      }
    })();
  }, [id, navigate]);

  const current = flatCases[index];
  const total = flatCases.length;
  const doneCount = results.length;
  const progressPct = total ? (index / total) * 100 : 0;

  const resultForCurrent = useMemo(() => {
    if (!current) return null;
    return results.find((r) => r.test_id === current.id);
  }, [current, results]);

  useEffect(() => {
    setNotes(resultForCurrent?.notes || "");
    caseStartRef.current = Date.now();
  }, [current?.id, resultForCurrent?.id]);

  // Live session + per-case timer (updates every second)
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Presence heartbeat: notify server every 15s that this tester is active on this session
  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const beat = async () => {
      try {
        await api.heartbeat(id);
      } catch {
        /* silent */
      }
    };
    beat();
    const t = setInterval(() => mounted && beat(), 15000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [id]);

  const submitResult = async (status, continue_after_fail = false) => {
    if (!current) return;
    setSubmitting(true);
    try {
      const r = await api.addResult(id, {
        test_id: current.id,
        status,
        notes,
        continue_after_fail,
      });
      const newResults = [...results.filter((x) => x.test_id !== current.id), r];
      setResults(newResults);

      const shouldAdvance =
        status === "pass" ||
        status === "skipped" ||
        status === "blocked" ||
        (status === "fail" && continue_after_fail);

      if (shouldAdvance) {
        const next = Math.min(index + 1, total);
        setIndex(next);
        if (next >= total) {
          toast.success("All test cases done — proceed to summary");
        } else {
          toast.success(`${current.id} marked ${status.toUpperCase()}`);
        }
      } else {
        // Fail + no continue = HARD STOP
        toast.error(`${current.id} FAILED — run paused`);
      }
    } catch (e) {
      toast.error("Failed to save result");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFail = () => setFailDialogOpen(true);

  const handleFinish = () => navigate(`/session/${id}/summary`);

  // Keyboard shortcuts: P = pass, F = fail (opens dialog), S = skip,
  // B = blocked, N = focus notes, → / Enter = next (pass), Esc closes dialogs.
  useEffect(() => {
    const onKey = (e) => {
      if (failDialogOpen) return;
      const t = e.target;
      const tag = t?.tagName;
      const isTyping =
        tag === "TEXTAREA" ||
        (tag === "INPUT" && t.type !== "checkbox" && t.type !== "radio");
      if (isTyping) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!current) return;
      const key = e.key.toLowerCase();
      if (key === "p") {
        e.preventDefault();
        submitResult("pass");
      } else if (key === "f") {
        e.preventDefault();
        setFailDialogOpen(true);
      } else if (key === "s") {
        e.preventDefault();
        submitResult("skipped");
      } else if (key === "b") {
        e.preventDefault();
        submitResult("blocked");
      } else if (key === "n") {
        e.preventDefault();
        notesRef.current?.focus();
      } else if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        submitResult("pass");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, failDialogOpen, notes, results, index]);

  if (!checklist || !session) {
    return (
      <div className="p-10 text-center text-slate-500">Loading runner…</div>
    );
  }

  const runComplete = index >= total || !current;
  const isRegression = runComplete ? false : Boolean(current.is_regression);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      {/* Sidebar */}
      <aside className="lg:col-span-1 space-y-6">
        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-3">Progress</div>
          <div className="flex items-baseline gap-2 mb-2">
            <span
              data-testid="progress-count"
              className="font-heading text-3xl font-bold tracking-tight"
            >
              {doneCount}
            </span>
            <span className="text-slate-500 text-sm">/ {total} logged</span>
          </div>
          <Progress value={progressPct} className="h-1.5 rounded-none bg-slate-100" />
          <div className="mt-2 text-xs text-slate-500 font-mono">
            Current: {Math.min(index + 1, total)} of {total}
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-3">Tester</div>
          <div className="font-medium text-slate-900">{session.tester_name}</div>
          <div className="mt-1 text-xs text-slate-500">
            {session.browser || "—"} · {session.device || "—"}
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-3 flex items-center gap-2">
            <Timer size={12} className="text-keplerio-teal" /> Timer
          </div>
          <div className="flex items-baseline justify-between">
            <span
              className="text-[10px] uppercase tracking-widest text-slate-400"
              title="Elapsed on current test case"
            >
              Case
            </span>
            <span
              data-testid="timer-case"
              className="font-mono text-lg text-keplerio-navy"
            >
              {formatDuration(now - caseStartRef.current)}
            </span>
          </div>
          <div className="mt-2 flex items-baseline justify-between border-t border-slate-100 pt-2">
            <span
              className="text-[10px] uppercase tracking-widest text-slate-400"
              title="Elapsed since session start"
            >
              Session
            </span>
            <span
              data-testid="timer-session"
              className="font-mono text-lg text-keplerio-navy"
            >
              {formatDuration(now - new Date(session.started_at).getTime())}
            </span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-3 flex items-center gap-2">
            <Keyboard size={12} /> Shortcuts
          </div>
          <ul className="space-y-1.5 text-xs">
            <ShortcutRow keys={["P"]} label="Pass" />
            <ShortcutRow keys={["F"]} label="Fail" />
            <ShortcutRow keys={["S"]} label="Skip" />
            <ShortcutRow keys={["B"]} label="Blocked" />
            <ShortcutRow keys={["N"]} label="Focus notes" />
            <ShortcutRow keys={["↵", "→"]} label="Next (pass)" />
          </ul>
        </div>

        <div className="bg-white border border-slate-200 p-5">
          <div className="overline mb-3">Sections</div>
          <ul className="space-y-1.5">
            {checklist.sections.map((s) => {
              const passed = s.cases.filter((c) =>
                results.find((r) => r.test_id === c.id && r.status === "pass")
              ).length;
              const failed = s.cases.filter((c) =>
                results.find((r) => r.test_id === c.id && r.status === "fail")
              ).length;
              const inCurrent = current ? current.sectionNumber === s.number : false;
              return (
                <li
                  key={s.id}
                  data-testid={`sidebar-section-${s.number}`}
                  className={`px-2 py-1.5 text-xs flex items-center justify-between border-l-2 ${
                    inCurrent ? "border-keplerio-teal bg-keplerio-teal/10" : "border-transparent"
                  }`}
                >
                  <span className="text-slate-700">
                    <span className="font-mono text-slate-400 mr-1">{s.number}.</span>
                    {s.title}
                  </span>
                  <span className="text-slate-500 font-mono">
                    {passed > 0 && <span className="text-emerald-700">{passed}✓</span>}
                    {failed > 0 && <span className="text-red-700 ml-1">{failed}✗</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:col-span-3">
        {runComplete ? (
          <div className="bg-white border border-slate-200 p-10 text-center">
            <div className="w-12 h-12 bg-keplerio-teal mx-auto mb-4 flex items-center justify-center">
              <ListChecks size={22} className="text-white" />
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-keplerio-navy">
              All test cases logged
            </h2>
            <p className="mt-2 text-slate-600 text-sm max-w-md mx-auto">
              Review the session, add an overall recommendation, and export the CSV.
            </p>
            <Button
              onClick={handleFinish}
              data-testid="go-to-summary-btn"
              className="mt-6 bg-keplerio-navy hover:bg-keplerio-navy-dark text-white rounded-none h-11 px-6"
            >
              Go to summary
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Breadcrumb */}
            <div className="flex items-center text-xs text-slate-500 font-mono">
              <span>Section {current.sectionNumber}</span>
              <ChevronRight size={12} className="mx-1" />
              <span>{current.sectionTitle}</span>
            </div>

            {/* Test Card */}
            <div className="bg-white border border-slate-200">
              <div className="p-6 md:p-8 border-b border-slate-100">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="overline text-blue-700 mb-2" data-testid="test-case-id">
                      Test Case · {current.id}
                    </div>
                    <h2
                      data-testid="test-title"
                      className="font-heading text-2xl md:text-3xl font-bold tracking-tight"
                    >
                      {current.title}
                    </h2>
                  </div>
                  {resultForCurrent && (
                    <ResultBadge status={resultForCurrent.status} />
                  )}
                </div>
              </div>

              <div className={`p-6 md:p-8 grid grid-cols-1 ${current.preconditions ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6 border-b border-slate-100`}>
                {current.preconditions && (
                  <Field label="Preconditions" value={current.preconditions} />
                )}
                <Field label="Steps" value={current.steps} />
                <Field label="Expected result" value={current.expected} />
              </div>

              {isRegression && (
                <div
                  data-testid="regression-callout"
                  className="mx-6 md:mx-8 my-6 border border-keplerio-gold bg-keplerio-gold/10 p-4"
                >
                  <div className="overline text-keplerio-gold-dark mb-1">
                    Regression Check
                  </div>
                  <p className="text-sm text-slate-800">
                    The old{" "}
                    <span
                      data-testid="regression-number"
                      className="font-mono font-bold text-keplerio-navy"
                    >
                      {checklist.regression_number}
                    </span>{" "}
                    UAT Auth Check screen should <strong>NOT</strong> appear during a
                    normal auth journey. If it does, mark this run as a failure.
                  </p>
                </div>
              )}

              <div className="p-6 md:p-8">
                <label className="overline block mb-2">Notes (optional)</label>
                <Textarea
                  ref={notesRef}
                  data-testid="notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything unusual? Bug refs, screenshots reference, environment quirks…"
                  className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-blue-500 min-h-[92px]"
                />

                <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-between">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => submitResult("pass")}
                      disabled={submitting}
                      data-testid="pass-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none h-11 px-6"
                    >
                      <Check size={16} className="mr-2" />
                      Pass
                    </Button>
                    <Button
                      onClick={handleFail}
                      disabled={submitting}
                      data-testid="fail-btn"
                      className="bg-red-600 hover:bg-red-700 text-white rounded-none h-11 px-6"
                    >
                      <X size={16} className="mr-2" />
                      Fail
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => submitResult("skipped")}
                      disabled={submitting}
                      data-testid="skip-btn"
                      className="rounded-none h-11 px-4 border-slate-300"
                    >
                      <SkipForward size={16} className="mr-2" />
                      Skip
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => submitResult("blocked")}
                      disabled={submitting}
                      data-testid="blocked-btn"
                      className="rounded-none h-11 px-4 border-slate-300"
                    >
                      <Ban size={16} className="mr-2" />
                      Blocked
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end pt-2">
              <Button
                onClick={handleFinish}
                variant="outline"
                data-testid="end-early-btn"
                className="rounded-none border-slate-300 h-10"
              >
                <Flag size={14} className="mr-2" />
                End session early
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Fail dialog */}
      <AlertDialog open={failDialogOpen} onOpenChange={setFailDialogOpen}>
        <AlertDialogContent className="rounded-none border border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">
              Mark {current?.id} as failed?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You can stop the run here to investigate, or continue to the next test case.
              Either way this result and any notes are saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 flex-wrap">
            <AlertDialogCancel
              data-testid="fail-dialog-cancel"
              className="rounded-none border-slate-300"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              data-testid="fail-stop-btn"
              onClick={async () => {
                setFailDialogOpen(false);
                await submitResult("fail", false);
              }}
              className="rounded-none border-red-300 text-red-700 hover:bg-red-50"
            >
              Fail &amp; stop here
            </Button>
            <AlertDialogAction
              data-testid="fail-continue-btn"
              onClick={async () => {
                setFailDialogOpen(false);
                await submitResult("fail", true);
              }}
              className="rounded-none bg-keplerio-navy text-white hover:bg-keplerio-navy-dark"
            >
              Fail &amp; continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      <p className="text-sm text-slate-700 leading-relaxed break-words">{value}</p>
    </div>
  );
}

function ResultBadge({ status }) {
  const map = {
    pass: { label: "Pass", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    fail: { label: "Fail", cls: "bg-red-100 text-red-800 border-red-200" },
    skipped: { label: "Skipped", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    blocked: { label: "Blocked", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <Badge className={`rounded-none border ${map.cls} font-medium`}>
      Prior: {map.label}
    </Badge>
  );
}

function ShortcutRow({ keys, label }) {
  return (
    <li className="flex items-center justify-between text-slate-600">
      <span>{label}</span>
      <span className="flex gap-1">
        {keys.map((k, i) => (
          <kbd
            key={i}
            className="font-mono text-[10px] bg-slate-100 border border-slate-200 px-1.5 py-0.5 min-w-[20px] text-center text-keplerio-navy"
          >
            {k}
          </kbd>
        ))}
      </span>
    </li>
  );
}
